import React from "react";
import { Search, X } from "lucide-react";

/**
 * ProjectSearchBar — Feb 2026
 * Full-width search input placed above a role's primary project list.
 * Real-time filter is done in the parent (via `useMemo`) so callers stay
 * in control of what fields to match against. This component is purely
 * presentational + a small clear (×) button.
 *
 * Props:
 *   value          — current query string
 *   onChange(str)  — called on every keystroke
 *   placeholder    — override placeholder text
 *   resultCount    — filtered result count (rendered on the right)
 *   totalCount     — unfiltered result count (rendered as `X / Y`)
 *   testid         — data-testid for the input
 */
export default function ProjectSearchBar({
  value,
  onChange,
  placeholder = "Search projects by name, client, or city…",
  resultCount,
  totalCount,
  testid = "project-search",
}) {
  const showCounts = typeof resultCount === "number" && typeof totalCount === "number";
  return (
    <div className="flex items-center gap-3 mb-4" data-testid={`${testid}-container`}>
      <div className="relative flex-1">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none"
          strokeWidth={1.5}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="iz-input pl-10 pr-10 w-full"
          data-testid={testid}
          aria-label="Search projects"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#262626] text-gray-500 hover:text-[#d4af37] transition-colors"
            aria-label="Clear search"
            data-testid={`${testid}-clear`}
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        )}
      </div>
      {showCounts && (
        <span
          className="text-xs font-mono uppercase tracking-widest text-gray-500 shrink-0"
          data-testid={`${testid}-count`}
        >
          {resultCount} / {totalCount}
        </span>
      )}
    </div>
  );
}

/**
 * ProjectSearchEmpty — subtle empty state to render when a search
 * has zero matches. Callers put this in place of their list body.
 */
export function ProjectSearchEmpty({ query, testid = "project-search-empty" }) {
  return (
    <div
      className="border border-dashed border-[#262626] rounded-md p-10 text-center bg-[#050505]"
      data-testid={testid}
    >
      <Search className="w-6 h-6 text-gray-700 mx-auto mb-2" strokeWidth={1.5} />
      <p className="text-sm text-gray-400">
        No projects match{" "}
        <span className="text-[#d4af37] font-mono">"{query}"</span>
      </p>
      <p className="text-[11px] text-gray-600 mt-1 font-mono uppercase tracking-widest">
        Try a different project name, client, or city
      </p>
    </div>
  );
}

/**
 * Utility: default matcher — case-insensitive substring on project_name,
 * client_name, city. Consumers can pass extra keys via `extraKeys` to
 * broaden the match (e.g. `["flat_no"]` on IMOS orders).
 */
export function projectMatches(row, query, extraKeys = []) {
  if (!query || !query.trim()) return true;
  const q = query.toLowerCase().trim();
  const fields = ["project_name", "client_name", "city", ...extraKeys];
  return fields.some((k) => {
    const v = row?.[k];
    return typeof v === "string" && v.toLowerCase().includes(q);
  });
}
